import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";
import { UNREAD_WORK_ORDER_COUNT_REFRESH_INTERVAL_MS } from "../lib/activePageRefresh";
import type { CustomerId, FacilityId } from "../lib/workIds";

// Rooted at the shared Work Orders key so the existing Work Order mutation
// invalidations refresh the badge without each mutation naming it.
export const unreadWorkOrderCountQueryKey = [
  "work-orders",
  "unread-count",
] as const;

const unreadWorkOrderCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

async function fetchUnreadWorkOrderCount(
  customerId: CustomerId,
  facilityId: FacilityId,
): Promise<number> {
  const response = await workClient.v1.customers[":customerId"].facilities[
    ":facilityId"
  ]["work-orders"]["unread-count"].$get({
    param: { customerId, facilityId },
  });
  if (!response.ok) {
    throw new Error(
      `Failed to fetch the unread Work Order count: ${response.status}`,
    );
  }
  const payload = unreadWorkOrderCountResponseSchema.parse(
    await response.json(),
  );
  return payload.count;
}

export function unreadWorkOrderCountQueryOptions(
  customerId: CustomerId | undefined,
  facilityId: FacilityId | undefined,
) {
  return queryOptions({
    queryKey: [...unreadWorkOrderCountQueryKey, customerId, facilityId],
    queryFn: () => {
      if (!customerId || !facilityId) {
        throw new Error("A customer and facility must be selected.");
      }
      return fetchUnreadWorkOrderCount(customerId, facilityId);
    },
    refetchInterval: UNREAD_WORK_ORDER_COUNT_REFRESH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    staleTime: 0,
  });
}

export function useUnreadWorkOrderCountQuery(options?: { enabled?: boolean }) {
  const { selectedCustomer, selectedFacility } = useWorkScope();

  return useQuery({
    ...unreadWorkOrderCountQueryOptions(
      selectedCustomer?.id,
      selectedFacility?.id,
    ),
    enabled:
      Boolean(selectedCustomer && selectedFacility) &&
      (options?.enabled ?? true),
  });
}
